import { Capacitor } from '@capacitor/core'
import { isPlanAtLeast, type MembershipPlan } from './subscriptionPolicy'
import { initializeNativePurchases } from './platform'
import { productIdForOffer } from './storeProducts'
import type { CustomerInfo } from '@revenuecat/purchases-capacitor'

export type { MembershipPlan } from './subscriptionPolicy'
export type MembershipBillingPeriod = 'monthly' | 'yearly' | null
export type MembershipStatus =
  | 'free'
  | 'active'
  | 'trial'
  | 'grace'
  | 'cancelled'

export interface MembershipProfile {
  plan: MembershipPlan
  status: MembershipStatus
  billingPeriod: MembershipBillingPeriod
  memberSinceMs: number
  planStartedAtMs: number
  renewalAtMs: number | null
  creditsResetAtMs: number
  willRenew: boolean
}

function validDateMs(value: string | number | undefined | null): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value
  }
  if (!value) return null
  const parsed = Date.parse(String(value))
  return Number.isFinite(parsed) ? parsed : null
}

export function freeMembershipProfile(
  creationTime?: string | null,
): MembershipProfile {
  const memberSinceMs = validDateMs(creationTime) ?? Date.now()
  return {
    plan: 'free',
    status: 'free',
    billingPeriod: null,
    memberSinceMs,
    planStartedAtMs: memberSinceMs,
    renewalAtMs: null,
    creditsResetAtMs: 0,
    willRenew: false,
  }
}

export function nextMonthlyCreditResetAt(
  anchorMs: number,
  fromMs = Date.now(),
): number {
  const anchor = new Date(anchorMs)
  const from = new Date(fromMs)
  const hour = anchor.getUTCHours()
  const minute = anchor.getUTCMinutes()
  const second = anchor.getUTCSeconds()
  const millisecond = anchor.getUTCMilliseconds()

  const candidateFor = (year: number, month: number) => {
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
    return Date.UTC(
      year,
      month,
      Math.min(anchor.getUTCDate(), lastDay),
      hour,
      minute,
      second,
      millisecond,
    )
  }

  let candidate = candidateFor(from.getUTCFullYear(), from.getUTCMonth())
  if (candidate <= fromMs) {
    candidate = candidateFor(from.getUTCFullYear(), from.getUTCMonth() + 1)
  }
  return candidate
}

const freePlanLabels: Record<string, string> = {
  tr: 'Ücretsiz üye', en: 'Free', de: 'Kostenlos', es: 'Gratis', fr: 'Gratuit', ru: 'Бесплатный',
  it: 'Gratuito', pt: 'Grátis', sv: 'Gratis', nl: 'Gratis', ar: 'مجاني', hi: 'निःशुल्क',
  id: 'Gratis', ja: '無料', ko: '무료', pl: 'Bezpłatny', th: 'ฟรี',
}

const periodLabels: Record<string, { monthly: string; yearly: string }> = {
  tr: { monthly: 'Aylık', yearly: 'Yıllık' }, en: { monthly: 'Monthly', yearly: 'Yearly' },
  de: { monthly: 'Monatlich', yearly: 'Jährlich' }, es: { monthly: 'Mensual', yearly: 'Anual' },
  fr: { monthly: 'Mensuel', yearly: 'Annuel' }, ru: { monthly: 'Ежемесячно', yearly: 'Ежегодно' },
  it: { monthly: 'Mensile', yearly: 'Annuale' }, pt: { monthly: 'Mensal', yearly: 'Anual' },
  sv: { monthly: 'Månadsvis', yearly: 'Årsvis' }, nl: { monthly: 'Maandelijks', yearly: 'Jaarlijks' },
  ar: { monthly: 'شهري', yearly: 'سنوي' }, hi: { monthly: 'मासिक', yearly: 'वार्षिक' },
  id: { monthly: 'Bulanan', yearly: 'Tahunan' }, ja: { monthly: '月額', yearly: '年額' },
  ko: { monthly: '월간', yearly: '연간' }, pl: { monthly: 'Miesięcznie', yearly: 'Rocznie' },
  th: { monthly: 'รายเดือน', yearly: 'รายปี' },
}

function languageCode(locale = 'tr-TR') {
  return locale.toLowerCase().split('-')[0] || 'en'
}

export function membershipPlanLabel(profile: MembershipProfile | MembershipPlan, locale = 'tr-TR'): string {
  const plan = typeof profile === 'string' ? profile : profile.plan
  const code = languageCode(locale)
  if (plan === 'studio') return code === 'tr' ? 'Studio üye' : 'Studio'
  if (plan === 'pro' || plan === 'plus') return code === 'tr' ? 'Pro üye' : 'Pro'
  return freePlanLabels[code] ?? freePlanLabels.en!
}

export function membershipPeriodLabel(
  period: MembershipBillingPeriod,
  locale = 'tr-TR',
): string | null {
  const labels = periodLabels[languageCode(locale)] ?? periodLabels.en!
  if (period === 'yearly') return labels.yearly
  if (period === 'monthly') return labels.monthly
  return null
}

export function formatMembershipDate(value: number | null, locale = 'tr-TR'): string {
  if (!value) return '—'
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Istanbul',
  }).format(new Date(value))
}

export function formatCreditResetTime(value: number, locale = 'tr-TR'): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Istanbul',
  }).format(new Date(value))
}

function planFromProduct(value: string): Exclude<MembershipPlan, 'free'> | null {
  if (/studio/i.test(value)) return 'studio'
  if (/(plus|starter)/i.test(value)) return 'plus'
  return /pro/i.test(value) ? 'pro' : null
}

function periodFromProduct(value: string): Exclude<MembershipBillingPeriod, null> {
  return /(annual|year|yearly|yıllık|12m)/i.test(value) ? 'yearly' : 'monthly'
}

export async function resolveNativeMembershipProfile(
  userId: string,
  fallback: MembershipProfile,
): Promise<MembershipProfile> {
  if (!Capacitor.isNativePlatform()) return fallback

  const revenueCatKey =
    Capacitor.getPlatform() === 'ios'
      ? import.meta.env.VITE_REVENUECAT_IOS_PUBLIC_KEY
      : import.meta.env.VITE_REVENUECAT_ANDROID_PUBLIC_KEY
  if (!revenueCatKey) return fallback

  try {
    await initializeNativePurchases()
    const { Purchases } = await import('@revenuecat/purchases-capacitor')
    const identity = await Purchases.getAppUserID()
    let { customerInfo } = identity.appUserID === userId
      ? await Purchases.getCustomerInfo()
      : await Purchases.logIn({ appUserID: userId })
    const syncKey = `voxnela:store-sync:v2:${userId}`
    if (!membershipProfileFromCustomerInfo(customerInfo, fallback) && !localStorage.getItem(syncKey)) {
      await Purchases.syncPurchases()
      customerInfo = (await Purchases.getCustomerInfo()).customerInfo
      localStorage.setItem(syncKey, 'true')
    }
    return membershipProfileFromCustomerInfo(customerInfo, fallback) ?? freeMembershipProfile(new Date(fallback.memberSinceMs).toISOString())
  } catch (error) {
    console.warn(
      '[VOXNELA_MEMBERSHIP] RevenueCat üyelik bilgisi alınamadı.',
      error,
    )
    return fallback
  }
}

export function withMembershipTimeout<T>(promise: Promise<T>, durationMs = 12_000): Promise<T> {
  let timer: ReturnType<typeof setTimeout>
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error('Membership request timed out.')), durationMs)
    }),
  ]).finally(() => clearTimeout(timer))
}

export function membershipProfileFromCustomerInfo(
  customerInfo: CustomerInfo,
  fallback: MembershipProfile,
): MembershipProfile | null {
  const activeEntitlements = Object.values(customerInfo.entitlements.active)
  const products = [...new Set([
    ...customerInfo.activeSubscriptions,
    ...activeEntitlements.map((item) => item.productIdentifier),
  ])].filter((product) => planFromProduct(product))
  const rank = (product: string) => planFromProduct(product) === 'studio' ? 2 : 1
  products.sort((a, b) => rank(b) - rank(a))
  const product = products[0]
  if (!product) return null
  const plan = planFromProduct(product)!
  const entitlement = activeEntitlements.find((item) => item.productIdentifier === product)
  const subscription = customerInfo.subscriptionsByProductIdentifier?.[product]
  const startedAtMs = validDateMs(entitlement?.originalPurchaseDateMillis) ??
    validDateMs(entitlement?.originalPurchaseDate) ??
    validDateMs(subscription?.originalPurchaseDate) ??
    validDateMs(subscription?.purchaseDate) ??
    validDateMs(customerInfo.allPurchaseDates[product]) ?? fallback.planStartedAtMs
  const willRenew = entitlement?.willRenew ?? subscription?.willRenew ?? true
  const billingIssue = entitlement?.billingIssueDetectedAt ?? subscription?.billingIssuesDetectedAt
  const periodType = entitlement?.periodType ?? subscription?.periodType
  return {
    plan,
    status: String(periodType).toLowerCase() === 'trial' ? 'trial' : billingIssue ? 'grace' : willRenew ? 'active' : 'cancelled',
    billingPeriod: periodFromProduct(product),
    memberSinceMs: fallback.memberSinceMs,
    planStartedAtMs: startedAtMs,
    renewalAtMs: validDateMs(entitlement?.expirationDateMillis) ??
      validDateMs(entitlement?.expirationDate) ?? validDateMs(customerInfo.allExpirationDates[product]),
    creditsResetAtMs: nextMonthlyCreditResetAt(startedAtMs),
    willRenew,
  }
}

export async function restoreNativeMembershipProfile(userId: string, fallback: MembershipProfile): Promise<MembershipProfile | null> {
  await initializeNativePurchases()
  const { Purchases } = await import('@revenuecat/purchases-capacitor')
  await Purchases.logIn({ appUserID: userId })
  const { customerInfo } = await Purchases.restorePurchases()
  return membershipProfileFromCustomerInfo(customerInfo, fallback)
}

export function reconcileMembershipWorkspace<T extends { membership: MembershipProfile; credits: number }>(
  workspace: T,
  nativeMembership: MembershipProfile,
): T & { membershipSyncPending: boolean } {
  const membershipSyncPending = nativeMembership.plan !== 'free' &&
    !isPlanAtLeast(workspace.membership.plan, nativeMembership.plan)
  return {
    ...workspace,
    membership: membershipSyncPending ? nativeMembership : workspace.membership,
    credits: membershipSyncPending ? 0 : workspace.credits,
    membershipSyncPending,
  }
}

export async function purchaseNativeMembershipPlan({
  userId,
  plan,
  billingPeriod,
}: {
  userId: string
  plan: Exclude<MembershipPlan, 'free'>
  billingPeriod: Exclude<MembershipBillingPeriod, null>
}): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    throw new Error(
      'Web ödeme akışı henüz etkin değil. Satın alma işlemini iOS veya Android uygulamasından tamamlayabilirsin.',
    )
  }
  const revenueCatKey =
    Capacitor.getPlatform() === 'ios'
      ? import.meta.env.VITE_REVENUECAT_IOS_PUBLIC_KEY
      : import.meta.env.VITE_REVENUECAT_ANDROID_PUBLIC_KEY
  if (!revenueCatKey) {
    throw new Error(
      'Mağaza ödeme bağlantısı bu yapıda etkin değil. Lütfen destek ekibiyle iletişime geç.',
    )
  }

  await initializeNativePurchases()
  const { Purchases } = await import('@revenuecat/purchases-capacitor')
  await Purchases.logIn({ appUserID: userId })

  if (Capacitor.getPlatform() === 'ios') {
    const productId = productIdForOffer(plan, billingPeriod)
    const { products } = await Purchases.getProducts({
      productIdentifiers: [productId],
    })
    const product = products.find((item) => item.identifier === productId)
    if (!product) {
      throw new Error(
        `${plan === 'studio' ? 'Studio' : 'Pro'} ${billingPeriod === 'yearly' ? 'yıllık' : 'aylık'} App Store ürünü bulunamadı.`,
      )
    }
    await Purchases.purchaseStoreProduct({ product })
    return
  }

  const offerings = await Purchases.getOfferings()
  const packages = offerings.current?.availablePackages ?? []
  const periodPattern =
    billingPeriod === 'yearly'
      ? /(annual|year|yearly|12m|p1y)/i
      : /(month|monthly|1m|p1m)/i
  const target = packages.find((item) => {
    const searchable = [
      item.identifier,
      item.packageType,
      item.offeringIdentifier,
      item.product.identifier,
      item.product.subscriptionPeriod,
    ].join(' ')
    return new RegExp(plan, 'i').test(searchable) && periodPattern.test(searchable)
  })
  if (!target) {
    throw new Error(
      `${plan === 'plus' ? 'Plus' : plan === 'pro' ? 'Pro' : 'Studio'} ${billingPeriod === 'yearly' ? 'yıllık' : 'aylık'} mağaza ürünü bulunamadı. RevenueCat offering yapılandırmasını kontrol edin.`,
    )
  }
  await Purchases.purchasePackage({ aPackage: target })
}

export function isCurrentSubscription(
  currentPlan: MembershipPlan,
  currentPeriod: MembershipBillingPeriod,
  targetPlan: Exclude<MembershipPlan, 'free'>,
  targetPeriod: 'monthly' | 'yearly',
): boolean {
  if (currentPlan === 'free') return false
  const normalizedCurrent = currentPlan === 'plus' ? 'pro' : currentPlan
  const normalizedTarget = targetPlan === 'plus' ? 'pro' : targetPlan
  return normalizedCurrent === normalizedTarget && currentPeriod === targetPeriod
}

export function isPlanUpgradeAvailable(
  currentPlan: MembershipPlan,
  currentPeriod: MembershipBillingPeriod,
  targetPlan: Exclude<MembershipPlan, 'free'>,
  targetPeriod: 'monthly' | 'yearly',
): boolean {
  if (currentPlan === 'free') return true
  const planRank: Record<string, number> = { free: 0, plus: 1, pro: 1, studio: 2 }
  const currentRank = planRank[currentPlan] ?? 0
  const targetRank = planRank[targetPlan] ?? 0

  if (targetRank > currentRank) {
    return true
  }
  if (targetRank === currentRank) {
    // Aynı kademede sadece aylıktan yıllığa geçiş yükseltmedir
    return currentPeriod === 'monthly' && targetPeriod === 'yearly'
  }
  return false
}
